import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ClipboardList } from 'lucide-react';
import { atlasApi, AtlasUser, AvaliacaoPendente, TipoAvaliacao } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
  onResponder: (cicloId: string, funccodi: number) => void;
}

const TIPO_LABEL: Record<TipoAvaliacao, string> = {
  auto: 'Autoavaliação', gestor: 'Como gestor(a)', par: 'Como par/colega', liderado: 'Como liderado(a)',
};
const TIPO_CLS: Record<TipoAvaliacao, string> = {
  auto: 'bg-purple-100 text-purple-700', gestor: 'bg-blue-100 text-blue-700',
  par: 'bg-amber-100 text-amber-700', liderado: 'bg-emerald-100 text-emerald-700',
};

const MinhasAvaliacoes: React.FC<Props> = ({ user, onResponder }) => {
  const [itens, setItens] = useState<AvaliacaoPendente[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getMinhasPendentes(user)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>;
  if (erro) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;

  return (
    <div className="max-w-3xl">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4"><ClipboardList className="w-5 h-5 text-rose-500" /> Minhas avaliações</h2>
      {itens.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">Sem avaliações pendentes. 🎉</div>
      ) : (
        <ul className="space-y-2">
          {itens.map((p, i) => {
            const auto = p.tipo_avaliacao === 'auto';
            return (
              <li key={`${p.ciclo_id}-${p.avaliado_funccodi}-${p.tipo_avaliacao}-${i}`} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
                <AvatarColaborador nome={auto ? (p.avaliado_nome || 'Você') : (p.avaliado_nome || `#${p.avaliado_funccodi}`)} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {auto ? 'Autoavaliação' : (p.avaliado_nome || `Colaborador #${p.avaliado_funccodi}`)}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{p.ciclo_nome}{p.avaliado_cargo ? ` · ${p.avaliado_cargo}` : ''}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${TIPO_CLS[p.tipo_avaliacao]}`}>{TIPO_LABEL[p.tipo_avaliacao]}</span>
                <button onClick={() => onResponder(p.ciclo_id, p.avaliado_funccodi)} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium">
                  Responder
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MinhasAvaliacoes;
